import { addDays, formatISO } from 'date-fns';
import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { feedback } from 'react-feedbacker';
import { useFieldArray, useForm } from 'react-hook-form';
import { useMutation } from 'urql';

import { useCurrencies } from '@/bits';
import {
  Button,
  Card,
  CardCloseButton,
  CardOptions,
  CountryField,
  DateTimeField,
  ErrorMessage,
  InlineIconButton,
  LocalModal,
  NakedForm,
  SelectField,
  SelectOption,
  SubmitButton,
  TextField,
  useModalContext,
} from '@/components';
import { TrashIcon } from '@/components/icons';
import { useTranslate } from '@/contexts';
import { ChallengeRewardCreateForm } from '@/forms';
import {
  BrandEnum,
  ChallengeExternalSystem,
  ChallengeRewardInputV2,
  ChallengeRewardTypeEnum,
  ChallengeRewardTypeInput,
  PaymentIntegrationEnum,
  Scalars,
} from '@/globalTypes';
import { useIsMounted } from '@/hooks';
import { useBrandValueOptions, useBrands } from '@/hooks/useBrands';
import { Nullable } from '@/types';
import { enumToSelectOptions } from '@/utils';
import { assert } from '@/utils/error';
import {
  CreateDepositChallengeMutation,
  CreateDepositChallengeMutationVariables,
  CreateSowChallengeMutation,
  CreateSowChallengeMutationVariables,
} from './__generated__/ChallengeCreateForm';
import { ChallengeType } from './types';

const query = graphql`
  query SanityChallengeCreateForm {
    sanityChallengeCreateForm {
      title {
        ...SanityLocaleString
      }
      freeSpinsRewardSummary {
        ...SanityLocaleString
      }
      moneyDropRewardSummary {
        ...SanityLocaleString
      }
      bonusMoneyDropRewardSummary {
        ...SanityLocaleString
      }
      dynamicFreeSpinsRewardSummary {
        ...SanityLocaleString
      }
      dynamicMoneyDropRewardSummary {
        ...SanityLocaleString
      }
      dynamicBonusMoneyDropRewardSummary {
        ...SanityLocaleString
      }
      dynamicBonusMoneyDropRewardDynamicPayoutSummary {
        ...SanityLocaleString
      }
    }
  }
`;

const createDepositChallengeMutation = gql`
  mutation CreateDepositChallenge(
    $challengeName: String!
    $startDate: OffsetDateTime!
    $endDate: OffsetDateTime
    $currencyCode: ISOCurrencyCode!
    $countries: [String!]!
    $brands: [BrandEnum!]!
    $challengeRewards: [ChallengeRewardInputV2!]!
    $challengeSystem: ChallengeExternalSystem!
    $paymentIntegration: PaymentIntegrationEnum
  ) {
    createDepositChallenge: createDepositChallengeV2(
      challengeName: $challengeName
      startDate: $startDate
      endDate: $endDate
      currencyCode: $currencyCode
      countries: $countries
      brands: $brands
      challengeRewards: $challengeRewards
      challengeSystem: $challengeSystem
      paymentIntegration: $paymentIntegration
    )
  }
`;

const createSowChallengeMutation = gql`
  mutation CreateSowChallenge(
    $challengeName: String!
    $startDate: OffsetDateTime!
    $currencyCode: ISOCurrencyCode!
    $challengeRewards: [ChallengeRewardInputV2!]!
  ) {
    createSowChallenge(
      challengeName: $challengeName
      startDate: $startDate
      currencyCode: $currencyCode
      challengeRewards: $challengeRewards
    )
  }
`;

type ChallengeTypeOptions = SelectOption & { value: ChallengeType };

export type ChallengeCreateFormValues = {
  challengeName: string;
  brands: BrandEnum[];
  countries: string[];
  challengeType: ChallengeType;
  challengeSystem: ChallengeExternalSystem;
  paymentIntegration?: PaymentIntegrationEnum;
  rewards: ChallengeRewardInputV2[] & { payoutType?: string };
  startsAt: Date;
  currencyCode: Scalars['ISOCurrencyCode']['input'];
  endsAt?: Date;
};

type FormValues = ChallengeCreateFormValues;

const pct = (mul?: number) => (mul ?? 0) * 100;

const useFormatRewardSummary = (
  form: Queries.SanityChallengeCreateFormQuery['sanityChallengeCreateForm'],
) => {
  const { t } = useTranslate();

  return useCallback(
    (rewardInput: ChallengeRewardTypeInput): string => {
      switch (rewardInput.challengeRewardType) {
        case ChallengeRewardTypeEnum.MoneyDropType:
          return t(form?.moneyDropRewardSummary, {
            ...rewardInput.moneyDrop,
          });
        case ChallengeRewardTypeEnum.FreeSpinsType:
          return t(form?.freeSpinsRewardSummary, {
            ...rewardInput.freeSpins,
          });
        case ChallengeRewardTypeEnum.BonusMoneyDropType:
          return t(form?.bonusMoneyDropRewardSummary, {
            ...rewardInput.bonusMoneyDrop,
          });
        case ChallengeRewardTypeEnum.DynamicFreeSpinsType:
          return t(form?.dynamicFreeSpinsRewardSummary, {
            ...rewardInput.dynamicFreeSpins,
            multiplier: pct(rewardInput.dynamicFreeSpins?.multiplier),
          });
        case ChallengeRewardTypeEnum.DynamicMoneyDropType:
          return t(form?.dynamicMoneyDropRewardSummary, {
            ...rewardInput.dynamicMoneyDrop,
            multiplier: pct(rewardInput.dynamicMoneyDrop?.multiplier),
          });
        case ChallengeRewardTypeEnum.DynamicBonusMoneyDropType: {
          if (rewardInput.dynamicBonusMoneyDrop?.maxPayoutMultiplier) {
            return t(form?.dynamicBonusMoneyDropRewardDynamicPayoutSummary, {
              ...rewardInput.dynamicBonusMoneyDrop,
              multiplier: pct(rewardInput.dynamicBonusMoneyDrop?.multiplier),
            });
          }
          return t(form?.dynamicBonusMoneyDropRewardSummary, {
            ...rewardInput.dynamicBonusMoneyDrop,
            multiplier: pct(rewardInput.dynamicBonusMoneyDrop?.multiplier),
          });
        }
        default:
          return '';
      }
    },
    [t, form],
  );
};

const useOptions = () => {
  return useMemo(() => {
    const challengeTypeOptions: ChallengeTypeOptions[] = [
      {
        label: 'Deposit Event',
        value: ChallengeType.Deposit,
      },
      {
        label: 'Complete SoW',
        value: ChallengeType.CompleteSoW,
      },
    ];

    const challengeSystemOptions = enumToSelectOptions(ChallengeExternalSystem);
    const paymentIntegrationOptions = enumToSelectOptions(
      PaymentIntegrationEnum,
    );

    return {
      challengeTypeOptions,
      challengeSystemOptions,
      paymentIntegrationOptions,
    };
  }, []);
};

type Props = {
  callback?: () => void;
  /** Values that override the defaults, e.g. when cloning a challenge */
  initialValues?: Partial<FormValues>;
  /** Shown above the form, e.g. to flag values that need a review */
  notice?: ReactNode;
};

const ChallengeCreateForm = ({ callback, initialValues, notice }: Props) => {
  const staticData =
    useStaticQuery<Queries.SanityChallengeCreateFormQuery>(query);

  const form = staticData.sanityChallengeCreateForm;
  assert(form, 'missing form data');

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const { t } = useTranslate();
  const isMounted = useIsMounted();
  const { close } = useModalContext();
  const { currencyOptions } = useCurrencies();

  const {
    challengeTypeOptions,
    challengeSystemOptions,
    paymentIntegrationOptions,
  } = useOptions();

  const [rewardModalIsOpen, setRewardModalIsOpen] = useState(false);
  const formatRewardSummary = useFormatRewardSummary(form);
  const { brands } = useBrands();
  const brandValueOptions = useBrandValueOptions(brands);

  const [createDepositChallengeState, createDepositChallenge] = useMutation<
    CreateDepositChallengeMutation,
    CreateDepositChallengeMutationVariables
  >(createDepositChallengeMutation);

  const [createSowChallengeState, createSowChallenge] = useMutation<
    CreateSowChallengeMutation,
    CreateSowChallengeMutationVariables
  >(createSowChallengeMutation);

  const defaultValues: FormValues = {
    challengeName: '',
    brands: [],
    countries: [],
    challengeType: ChallengeType.Deposit,
    challengeSystem: ChallengeExternalSystem.Internal,
    rewards: [],
    startsAt: new Date(),
    endsAt: addDays(new Date(), 14),
    currencyCode: 'EUR',
    ...initialValues,
  };

  const methods = useForm<FormValues>({
    defaultValues,
  });

  const { watch, control, formState } = methods;

  const challengeType = watch('challengeType');
  const currencyCode = watch('currencyCode');
  const isDepositCriteria = challengeType === ChallengeType.Deposit;

  const rewards = useFieldArray<FormValues, 'rewards'>({
    name: 'rewards',
    control,
  });

  const onSubmit = (values: FormValues) => {
    if (values.challengeType === ChallengeType.Deposit) {
      const variables: CreateDepositChallengeMutationVariables = {
        challengeName: values.challengeName,
        startDate: formatISO(values.startsAt),
        endDate: values.endsAt ? formatISO(values.endsAt) : null,
        countries: values.countries,
        brands: values.brands,
        challengeRewards: values.rewards,
        currencyCode: values.currencyCode,
        challengeSystem: values.challengeSystem,
        paymentIntegration: values.paymentIntegration,
      };

      return createDepositChallenge(variables).then((res) => {
        if (res.error?.message && isMounted) {
          return setErrorMessage(res.error.message);
        } else if (close) {
          feedback.success('Challenge created successfully');
          if (callback) {
            callback();
          }
          close();
        }
      });
    }

    if (values.challengeType === ChallengeType.CompleteSoW) {
      const variables: CreateSowChallengeMutationVariables = {
        challengeName: values.challengeName,
        startDate: formatISO(values.startsAt),
        challengeRewards: values.rewards,
        currencyCode: values.currencyCode,
      };

      return createSowChallenge(variables).then((res) => {
        if (res.error?.message && isMounted) {
          return setErrorMessage(res.error.message);
        } else if (close) {
          feedback.success('Challenge created successfully');
          if (callback) {
            callback();
          }
          close();
        }
      });
    }

    return null;
  };

  const isSubmitDisabled =
    createDepositChallengeState.fetching ||
    createSowChallengeState.fetching ||
    rewards.fields.length === 0 ||
    !formState.isValid;

  return (
    <Card
      size="lg"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <LocalModal
        content={
          rewardModalIsOpen ? (
            <ChallengeRewardCreateForm
              onCompleted={(reward) => rewards.append(reward)}
              showDynamicRewards={isDepositCriteria}
              challengeCurrency={isDepositCriteria ? currencyCode : null}
            />
          ) : null
        }
        ctx={{ close: () => setRewardModalIsOpen(false) }}
        isOpen={rewardModalIsOpen}
      />
      {notice}
      <div className="flex flex-col p-6 space-y-6">
        <NakedForm className="w-full" onSubmit={onSubmit} methods={methods}>
          <div className="flex sm:flex-row flex-col space-x-6 pb-6">
            <div className="w-full sm:w-1/3 space-y-4">
              <SelectField
                name="challengeType"
                title="Challenge Type"
                required
                options={challengeTypeOptions}
              />
              <SelectField
                name="challengeSystem"
                title="Challenge System"
                required
                options={challengeSystemOptions}
              />
              <TextField required name="challengeName" title="Challenge Name" />
              <div className="grid sm:grid-cols-2 gap-2">
                <DateTimeField required title="Start Date" name="startsAt" />
                <DateTimeField title="End Date" name="endsAt" />
              </div>
              {isDepositCriteria && (
                <>
                  <SelectField
                    title="Brands"
                    name="brands"
                    required={isDepositCriteria}
                    options={brandValueOptions.options}
                    isMulti
                    allowSelectAll
                  />
                  <CountryField
                    title="Countries"
                    name="countries"
                    required={isDepositCriteria}
                    isMulti
                    allowSelectAll
                  />
                  <SelectField
                    name="paymentIntegration"
                    title="Payment Integration"
                    options={paymentIntegrationOptions}
                    isClearable
                  />
                </>
              )}
              <SelectField
                name="currencyCode"
                required
                title="Currency"
                options={currencyOptions}
              />
            </div>
            <div className="flex-1">
              <label className="block tracking-wide text-gray-700 dark:text-white text-sm font-semibold truncate">
                Rewards Summary
              </label>
              <ul className="mt-2 !list-disc text-gray-900 dark:text-white text-sm font-semibold truncate items-center">
                {rewards.fields.map((reward, i) => (
                  <li key={reward.id}>
                    {formatRewardSummary(reward.challengeRewardTypeInput)}
                    <InlineIconButton onClick={() => rewards.remove(i)}>
                      <TrashIcon />
                    </InlineIconButton>
                  </li>
                ))}
              </ul>
              <Button
                type="button"
                variant="primary"
                onClick={() => setRewardModalIsOpen(true)}
              >
                Add Reward
              </Button>
            </div>
          </div>
          <ErrorMessage message={errorMessage} />
          <div className="flex justify-end">
            <SubmitButton
              value="Create Challenge"
              disabled={isSubmitDisabled}
            />
          </div>
        </NakedForm>
      </div>
    </Card>
  );
};

export default ChallengeCreateForm;
